// Хранилища платформы «браузер»: токен сессии и ключ устройства.
//
// IndexedDB, а не localStorage, по двум причинам: она умеет хранить `CryptoKey` как объект — приватный
// ключ остаётся непереносимым и в коде страницы не появляется ни разу; и она не отдаётся синхронно
// первому же скрипту, который решит пройтись по хранилищу.

import type { DeviceKeyStore, KeyAlgorithm, SecretStore } from "@yofis/core";

const dbName = "yofis";
const version = 1;
const secretsStore = "secrets";
const keysStore = "keys";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, version);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(secretsStore)) {
        db.createObjectStore(secretsStore);
      }
      if (!db.objectStoreNames.contains(keysStore)) {
        db.createObjectStore(keysStore);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB недоступна"));
  });
}

async function run<T>(store: string, mode: IDBTransactionMode, body: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = body(db.transaction(store, mode).objectStore(store));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("запрос к IndexedDB не выполнен"));
    });
  } finally {
    db.close();
  }
}

/** Токен сессии: переживает перезагрузку вкладки, но не выходит за пределы этого браузера. */
export class IdbSecretStore implements SecretStore {
  async get(key: string): Promise<string | undefined> {
    const value = await run<unknown>(secretsStore, "readonly", (s) => s.get(key));
    return typeof value === "string" ? value : undefined;
  }

  async set(key: string, value: string): Promise<void> {
    await run(secretsStore, "readwrite", (s) => s.put(value, key));
  }

  async delete(key: string): Promise<void> {
    await run(secretsStore, "readwrite", (s) => s.delete(key));
  }
}

/** Ключ устройства: пара `CryptoKey` кладётся как есть — приватная часть непереносима. */
export class IdbDeviceKeyStore implements DeviceKeyStore {
  async load(): Promise<{ algorithm: KeyAlgorithm; pair: CryptoKeyPair } | undefined> {
    const saved = await run<unknown>(keysStore, "readonly", (s) => s.get("device"));
    if (saved === undefined || saved === null || typeof saved !== "object") {
      return undefined;
    }
    const record = saved as { algorithm?: unknown; pair?: unknown };
    if (typeof record.algorithm !== "string" || record.pair === undefined) {
      return undefined;
    }
    return { algorithm: record.algorithm as KeyAlgorithm, pair: record.pair as CryptoKeyPair };
  }

  async save(algorithm: KeyAlgorithm, pair: CryptoKeyPair): Promise<void> {
    await run(keysStore, "readwrite", (s) => s.put({ algorithm, pair }, "device"));
  }

  async clear(): Promise<void> {
    await run(keysStore, "readwrite", (s) => s.delete("device"));
  }
}
